/* eslint-disable react/jsx-no-target-blank */
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  makeStyles,
} from '@material-ui/core';
import clsx from 'clsx';
import i18n from 'i18n';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useClearCacheCtx } from 'react-clear-cache';
import { Link } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import listItems, { GroupMenuPros } from './listItems';
import styles from './styles';

const useStyles = makeStyles(styles);

interface OwnProps {
  open: boolean;
  permissions?: PermissionsData;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  width: number;
  onOpen?: () => void;
  onClose?: () => void;
}

const SideNavBar: React.FC<OwnProps> = ({
  open,
  permissions,
  companySelected,
  role,
  width,
  onOpen,
  onClose,
}) => {
  const classes = useStyles();
  const { isLatestVersion, emptyCacheStorage } = useClearCacheCtx();

  const isPermissionEnabled = (key: string | boolean) => {
    if (typeof key === 'boolean') return key;
    return permissions && permissions[key];
  };

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (onClose) {
      onClose();
    }
    if (!isLatestVersion) {
      event.preventDefault();
      emptyCacheStorage();
      window.location.reload();
    }
  };

  const filteredItems = (list: GroupMenuPros[]) =>
    list
      .filter(
        ({ items }) =>
          items.filter(item => isPermissionEnabled(item?.key)).length > 0
      )
      .map(({ group, items }) => {
        const filtered = items.filter(item => isPermissionEnabled(item?.key));
        return { group, items: filtered };
      });

  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: open,
        [classes.drawerClose]: !open,
        [classes.drawerCloseMobile]: !open && width <= 600,
      })}
      classes={{
        paper: clsx({
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
          [classes.drawerCloseMobile]: !open && width <= 600,
        }),
      }}
      anchor="left"
      open={open}
      onClose={onClose}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
    >
      {filteredItems(listItems(companySelected, role)).map(
        ({ group, items }: GroupMenuPros) => (
          <List
            key={group}
            subheader={
              <ListSubheader
                className={clsx(classes.listHeader, {
                  [classes.listHeaderClose]: !open,
                })}
                disableSticky
              >
                {open ? i18n.t(group) : '-'}
              </ListSubheader>
            }
          >
            {items.map(item => (
              <ListItem
                id={`btn${item?.link.replace(/\/+/g, '-').toString()}`}
                disabled={item?.disable}
                key={item?.title}
                className={clsx(classes.menuOptions, {
                  [classes.itemCurrentSuperAdmin]:
                    inSuperAdminView(role, companySelected) &&
                    window.location.pathname === item?.link,
                  [classes.itemCurrentAdmin]:
                    inAdminView(role, companySelected) &&
                    window.location.pathname === item?.link,
                  [classes.itemCurrentOperator]:
                    inOperatorView(role, companySelected) &&
                    window.location.pathname === item?.link,
                  [classes.itemCurrent]:
                    window.location.pathname === item?.link,
                })}
                onClick={handleClickMenu}
                component={Link}
                to={item?.link}
                button
              >
                <ListItemIcon
                  className={clsx(classes.listItemIcon, {
                    [classes.listItemIconSuperAdmin]: inSuperAdminView(
                      role,
                      companySelected
                    ),
                  })}
                >
                  {item?.icon}
                </ListItemIcon>
                <ListItemText
                  primary={i18n.t(item?.title)}
                  className={clsx(classes.listItemText, {
                    [classes.hiddenClose]: !open,
                  })}
                />
              </ListItem>
            ))}
          </List>
        )
      )}
    </Drawer>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
}: ApplicationState) => ({
  permissions: data?.permissions,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps)(SideNavBar);
